import React, { useEffect, useMemo, useState } from 'react';
import { ChartColumn, Check, ChevronDown, ChevronUp, Inbox, Loader2, Network, SlidersHorizontal, CircleCheck, Info } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import ardealyMark from '../../../assets/ardealy-mark.svg';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { useBillingApi } from '../../../common/hooks/use-billing-api.hook';
import { useQuoteApi } from '../../../common/hooks/use-quote-api.hook';
import { QuoteRequest } from '../../../common/models/billing.model';
import { resolveCtaKind, classifyPlan } from './plan-cta.util';
import { EmbeddedUpgradeModal } from './components/embedded-upgrade-modal';
import { PaymentMethodModal } from './components/payment-method-modal';
import { QuoteConfiguratorModal } from './components/quote-configurator-modal';
import { VendorPlan, PlanFeatures, PlanLimits } from '../../../modules/superadmin/our-vendors/models/vendor.model';
import { PageContainer } from '../../../common/components/PageContainer';
import { NEW_LIMIT_KEYS, NewLimitKey, isKnownLimitKey, readLimit } from '../../../components/design-system/plan/plan-limits';
import { Interval, PricedPlan, applyOffer, money, monthlyPrice, monthsSaved, planHighlights, visibleLadder } from '../../../modules/vendor/pricing/plan-ladder';
import { AccessBanner } from '../../../modules/vendor/pricing/AccessBanner';

// smartAssistant and customSizeRugs are granted per vendor, not sold on a plan.
const FEATURE_LABELS: Record<keyof Omit<PlanFeatures, 'visualizationQuality' | 'smartAssistant' | 'customSizeRugs'>, string> = {
  apiIntegration: 'API Integration',
  emailAutomation: 'Email Automation',
  aiSpaceAnalysis: 'AI Space Analysis',
  seamlessGeneration: 'Seamless Generation',
  model3dGeneration: '3D Model Generation',
  advancedAnalytics: 'Advanced Analytics',
  customFields: 'Custom Fields',
  categoryManagement: 'Categories',
  productCardCustomization: 'Product Card Customization',
  contentStudio: 'Content Studio',
  socialScheduler: 'Social Scheduler',
  shopperChat: 'Shopper Chat',
  adsManager: 'Ads',
  aiProductEnrichment: 'AI Product Descriptions',
};

// English fallbacks; the Free-plan depth keys are labelled by the upgrade page's own limits table.
const LIMIT_LABELS: Partial<Record<keyof PlanLimits, string>> = {
  maxProducts: 'Products',
  maxSpaces: 'Spaces',
  maxEmailsPerMonth: 'Emails / month',
  maxSyncFlowsPerDay: 'Sync flows / day',
  maxAutomationRules: 'Automation rules',
  maxLeads: 'Leads',
  maxImportProducts: 'Imports / month',
  monthlyCredits: 'Credits / month',
  maxSocialAccounts: 'Social accounts',
  maxScheduledPostsPerMonth: 'Scheduled posts / month',
  maxVisualizationsPerMonth: 'Visualizations / month',
  maxSeats: 'Seats',
};

type FeatureKey = keyof Omit<PlanFeatures, 'visualizationQuality' | 'smartAssistant' | 'customSizeRugs'>;

// Product-module grouping — mirrors the quote configurator so the plan cards and
// the configurator read the same way. Each group is a toggleable module (a
// primary feature) with the usage limits + sub-features it governs. Catalog has
// no feature key (always included). Labels reuse the shared QUOTE.* i18n subtree.
interface ModuleGroup {
  id: string;
  feature?: FeatureKey;
  accent?: string;
  Icon?: React.ElementType; // neutral icon (Catalog) — modules use the brand mark + accent dot
  limits: (keyof PlanLimits)[];
  subFeatures: FeatureKey[];
  hasQuality?: boolean;
}
const MODULE_GROUPS: ModuleGroup[] = [
  { id: 'contentStudio', feature: 'contentStudio', accent: '#F59E0B', limits: ['monthlyCredits'], subFeatures: ['seamlessGeneration'] },
  { id: 'aiSpaceAnalysis', feature: 'aiSpaceAnalysis', accent: '#EF4444', limits: ['maxVisualizationsPerMonth'], subFeatures: ['model3dGeneration'], hasQuality: true },
  { id: 'socialScheduler', feature: 'socialScheduler', accent: '#3B82F6', limits: ['maxSocialAccounts', 'maxScheduledPostsPerMonth'], subFeatures: [] },
  { id: 'emailAutomation', feature: 'emailAutomation', accent: '#16A34A', limits: ['maxEmailsPerMonth', 'maxAutomationRules', 'maxLeads'], subFeatures: [] },
  { id: 'catalog', Icon: Network, limits: ['maxProducts', 'maxSeats', 'maxSpaces', 'maxImportProducts', 'maxSyncFlowsPerDay'], subFeatures: [] },
];
const ADDON_GROUPS: { id: string; keys: FeatureKey[]; Icon: React.ElementType }[] = [
  { id: 'apiIntegration', keys: ['apiIntegration'], Icon: Inbox },
  { id: 'advancedAnalytics', keys: ['advancedAnalytics'], Icon: ChartColumn },
  { id: 'catalogCustomization', keys: ['customFields', 'categoryManagement', 'productCardCustomization'], Icon: SlidersHorizontal },
];
const ADDON_FALLBACK: Record<string, string> = {
  apiIntegration: 'Product integration',
  advancedAnalytics: 'Advanced analytics',
  catalogCustomization: 'Catalog customization',
};
// English fallbacks for the module names (keeps titles consistent whether or not
// the i18n instance is initialized, e.g. in tests).
const MODULE_FALLBACK: Record<string, string> = {
  contentStudio: 'Content Studio',
  aiSpaceAnalysis: 'Visualizer',
  socialScheduler: 'Social Planner',
  emailAutomation: 'Email Marketing',
};
const groupThousands = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const formatLimit = (value: number | undefined): string => {
  if (value === undefined || value === null) return '—';
  if (value === -1) return 'Unlimited';
  return String(value);
};

const limitOf = (plan: VendorPlan, key: string): number =>
  readLimit((plan.limits as unknown as Record<string, unknown> | undefined)?.[key]);

const FEATURE_KEYS = new Set<string>(Object.keys(FEATURE_LABELS));

const formatPrice = (plan: VendorPlan): string | null => {
  if (plan.price === undefined || plan.price === null) return null;
  const symbol = (plan.currency || 'USD').toUpperCase() === 'USD' ? '$' : `${plan.currency} `;
  return `${symbol}${plan.price}`;
};

const quoteMoney = (q: QuoteRequest): string => {
  const symbol = (q.quotedCurrency || 'usd').toLowerCase() === 'usd' ? '$' : `${(q.quotedCurrency || '').toUpperCase()} `;
  const grouped = String(q.quotedAmount ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${symbol}${grouped}${q.quotedInterval ? ` / ${q.quotedInterval === 'year' ? 'year' : 'month'}` : ''}`;
};

/**
 * The one-time integration fee in force for a plan: a quote's value wins (an
 * explicit 0 there means sales waived it), otherwise the plan's list fee.
 * Mirrors resolveIntegrationFee on the API.
 */
const effectiveIntegrationFee = (plan: VendorPlan, quote?: QuoteRequest | null): number => {
  const raw = quote?.integrationFee === undefined || quote?.integrationFee === null
    ? plan.integrationFee
    : quote.integrationFee;
  const value = Math.round(Number(raw) || 0);
  return value > 0 ? value : 0;
};

const feeMoney = (amount: number, currency?: string | null): string => {
  const symbol = (currency || 'usd').toLowerCase() === 'usd' ? '$' : `${(currency || '').toUpperCase()} `;
  return `${symbol}${String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

// Brand mark + per-product accent dot (or a neutral icon) — same treatment as
// the quote configurator's module icons.
const GroupMark: React.FC<{ accent?: string; Icon?: React.ElementType; size?: number }> = ({ accent, Icon, size = 26 }) => (
  <span
    className="relative flex shrink-0 items-center justify-center bg-muted"
    style={{ width: size, height: size }}
  >
    {Icon ? (
      <Icon style={{ width: Math.round(size * 0.6), height: Math.round(size * 0.6) }} />
    ) : (
      <img
        src={ardealyMark}
        alt=""
        className="brightness-0 dark:invert"
        style={{ width: Math.round(size * 0.6), height: Math.round(size * 0.6) }}
      />
    )}
    {accent && <span className="absolute top-0.5 end-0.5 size-[5px] rounded-[2px]" style={{ backgroundColor: accent }} />}
  </span>
);

// ✓-in-a-circle / dash badge for a feature row (the circles were always circles).
const FeatureBadge: React.FC<{ enabled: boolean }> = ({ enabled }) => (
  <span
    className={cn(
      'flex size-[18px] shrink-0 items-center justify-center rounded-full',
      enabled ? 'bg-foreground' : 'border',
    )}
  >
    {enabled ? <Check className="size-3 text-background" /> : <span className="h-px w-2 bg-muted-foreground" />}
  </span>
);

const UpgradePage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const plansApi = usePlansApi();
  const billingApi = useBillingApi();
  const quoteApi = useQuoteApi();
  const { planSlug, hasActiveSubscription, plan: myPlan, isExpired } = useVendorPlan();
  // Monthly or yearly prices on the cards; the payment step starts on the same cycle.
  const [interval, setInterval] = useState<Interval>('month');

  // Why the vendor came: `?reason=maxLeads` (a limit) or `?reason=adsManager` (a feature).
  const reason = new URLSearchParams(location.search).get('reason');
  const reasonIsLimit = isKnownLimitKey(reason);
  const reasonIsFeature = !!reason && FEATURE_KEYS.has(reason);

  /** A limit's value on a plan, for the cards and the table — history in days, absent as unlimited. */
  const formatPlanLimit = (plan: VendorPlan, key: string): string => {
    const v = limitOf(plan, key);
    if (key === 'analyticsHistoryDays') return v < 0 ? t('FREE_PLAN.FULL_HISTORY') : t('FREE_PLAN.DAYS', { count: v });
    return v < 0 ? t('PLANS.UNLIMITED', 'Unlimited') : groupThousands(v);
  };

  // ── Grouped-feature helpers (mirror the configurator's product modules) ──────
  const groupTitle = (g: ModuleGroup): string =>
    g.id === 'catalog' ? t('QUOTE.CATALOG.TITLE', 'Catalog & team') : t(`QUOTE.MODULE.${g.feature}.TITLE`, MODULE_FALLBACK[g.id] || FEATURE_LABELS[g.feature!]);
  const addonTitle = (id: string): string => t(`QUOTE.ADDON.${id}.TITLE`, ADDON_FALLBACK[id] || id);
  const addonEnabled = (plan: VendorPlan, keys: FeatureKey[]): boolean => keys.every((k) => Boolean(plan.features?.[k]));

  const [plans, setPlans] = useState<VendorPlan[]>([]);
  const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  // Comparison-table accordion groups — expanded by default (undefined = open).
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (id: string) => setCollapsedGroups((p) => ({ ...p, [id]: !p[id] }));

  // A centered ✓ / — cell for a boolean feature in the comparison table.
  const tableCheck = (enabled: boolean) =>
    enabled ? (
      <span
        className="inline-flex size-5 items-center justify-center rounded-full bg-foreground"
        aria-label="included"
      >
        <Check className="size-3 text-background" />
      </span>
    ) : (
      <span className="text-muted-foreground" aria-label="not-included">—</span>
    );

  // Detail rows for a comparison-table group: usage limits, then quality, then
  // sub-feature toggles. Each row renders a value (limit/quality) or a ✓/— cell.
  const groupDetailRows = (g: ModuleGroup): { key: string; label: string; render: (p: VendorPlan) => React.ReactNode }[] => {
    const rows: { key: string; label: string; render: (p: VendorPlan) => React.ReactNode }[] = [];
    g.limits.forEach((k) => rows.push({
      key: k,
      label: t(`QUOTE.VOLUME.${k}.LABEL`, LIMIT_LABELS[k]),
      render: (p) => formatLimit(p.limits?.[k]),
    }));
    if (g.hasQuality) rows.push({
      key: 'quality',
      label: t('PLANS.TABLE.QUALITY', 'Render quality'),
      render: (p) => {
        const q = p.features?.visualizationQuality;
        return q ? t(`QUOTE.QUALITY.${q.toUpperCase()}`, `${q[0].toUpperCase()}${q.slice(1)}`) : '—';
      },
    });
    g.subFeatures.forEach((sf) => rows.push({
      key: sf,
      label: t(`QUOTE.EXTRA.${sf}.LABEL`, FEATURE_LABELS[sf]),
      render: (p) => tableCheck(Boolean(p.features?.[sf])),
    }));
    return rows;
  };

  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; plan?: VendorPlan }>({ open: false });
  const [changing, setChanging] = useState(false);
  const [upgradePlanId, setUpgradePlanId] = useState<string | null>(null);
  const [upgradeInterval, setUpgradeInterval] = useState<'month' | 'year'>('month');
  // The plan whose payment-method chooser (Card vs Wire) is open.
  const [checkoutPlan, setCheckoutPlan] = useState<VendorPlan | null>(null);
  // The Enterprise/sales-tier plan whose quote configurator is open.
  const [quotePlan, setQuotePlan] = useState<VendorPlan | null>(null);
  // The accepted quote whose (Stripe) embedded checkout is open.
  const [payQuoteId, setPayQuoteId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const plansData = (await plansApi.getPlans()) as VendorPlan[] | undefined;
        // The public ladder (docs/pricing-v2.md) plus the vendor's own plan, even when
        // it is not offered any more: "you are here" belongs next to "where you could go".
        // This vendor's own prices, when Ardealy set them (docs/pricing-v2.md, Offers).
        const mine: any = await Promise.resolve(plansApi.getMyPlan()).catch(() => null);
        setPlans(applyOffer(visibleLadder(plansData || [], planSlug, myPlan as VendorPlan), mine?.offer));
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
    quoteApi.getMyQuoteRequests()
      .then((rows) => setQuotes(rows || []))
      .catch(() => { /* no accepted-quote CTA on failure */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `?plan=growth` (from the plan wall, or a link in an email) opens that plan's
  // next step at once: the payment chooser, or the sales configurator.
  const wantedPlan = new URLSearchParams(location.search).get('plan');
  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    if (deepLinked || !wantedPlan || !plans.length) return;
    const target = plans.find((p) => p.slug === wantedPlan);
    setDeepLinked(true);
    if (!target || target.slug === planSlug) return;
    const tier = classifyPlan(target);
    if ((target as PricedPlan).onRequest) setQuotePlan(target);
    else if (tier === 'paid' && !hasActiveSubscription) setCheckoutPlan(target);
    else if (tier === 'sales') setQuotePlan(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, wantedPlan]);

  // Map plan id → an accepted, payable quote for that plan (won + billing ready
  // for its method), unless the vendor is already subscribed.
  const payableQuoteByPlan = useMemo(() => {
    const map: Record<string, QuoteRequest> = {};
    if (hasActiveSubscription) return map;
    for (const q of quotes) {
      if (q.status !== 'won' || !q.plan) continue;
      const ready = q.paymentMethod === 'wire'
        ? q.quotedAmount !== undefined && q.quotedAmount !== null
        : !!q.stripePriceId;
      if (ready && !map[q.plan]) map[q.plan] = q;
    }
    return map;
  }, [quotes, hasActiveSubscription]);

  const payAcceptedQuote = async (quote: QuoteRequest) => {
    if (quote.paymentMethod === 'wire') {
      try {
        await quoteApi.createQuoteWireInvoice(quote._id);
        navigate('/home/billing/plan'); // billing page — the wire invoice shows in history
      } catch {
        /* stay on the page; the vendor can retry */
      }
    } else {
      setPayQuoteId(quote._id);
    }
  };

  const handleChangePlan = async (plan: VendorPlan) => {
    setChanging(true);
    try {
      await plansApi.changeMyPlan(plan._id);
      window.location.reload();
    } catch {
      setChanging(false);
      setConfirmDialog({ open: false });
    }
  };

  // Redirect to Stripe hosted Checkout for a paid plan.
  // Upgrade opens the in-app embedded checkout (no redirect to Stripe's domain).
  const handleUpgradeComplete = () => {
    // Subscription is provisioned by the webhook — give it a moment, then reload
    // so the plan/usage reflect Pro.
    setTimeout(() => window.location.reload(), 2500);
  };

  // Redirect to the Stripe Billing Portal to manage an existing subscription.
  const handlePortal = async () => {
    setChanging(true);
    try {
      const { url } = await billingApi.createPortalSession();
      window.location.href = url;
    } catch {
      setChanging(false);
    }
  };

  // Enterprise/sales-tier plans open the in-app quote configurator (replaces the
  // old mailto) — the vendor picks features + volumes and we record + email the request.
  const handleContactSales = (plan: VendorPlan) => setQuotePlan(plan);

  // Tier-aware CTA for a plan card (routing logic lives in plan-cta.util for testability).
  const getCta = (plan: VendorPlan, isCurrent: boolean): { label: string; onClick: () => void; disabled: boolean; kind: string } => {
    // A plan below the vendor's current plan is a downgrade (disabled for now).
    const currentPlan = plans.find((p) => p.slug === planSlug);
    const isDowngrade = !!currentPlan && !isCurrent && (plan.sortOrder ?? 0) < (currentPlan.sortOrder ?? 0);
    // A running or ended trial is not chosen, it is moved on from: no button on its card.
    if (isCurrent && plan.pricingType === 'trial') {
      return { kind: 'current', label: isExpired ? t('PRICING.UPGRADE.TRIAL_ENDED') : t('PLANS.CURRENT_PLAN', 'Current plan'), onClick: () => {}, disabled: true };
    }
    const kind = resolveCtaKind(plan, { isCurrent, hasActiveSubscription, isDowngrade });
    switch (kind) {
      case 'current': return { kind, label: t('PLANS.CURRENT_PLAN', 'Current plan'), onClick: () => {}, disabled: true };
      case 'downgrade': return { kind, label: t('PLANS.DOWNGRADE', 'Downgrade'), onClick: () => {}, disabled: true };
      case 'portal': return { kind, label: t('PLANS.MANAGE_SUBSCRIPTION', 'Manage subscription'), onClick: handlePortal, disabled: false };
      case 'checkout':
        // Outside this vendor's offer, a sold plan is priced on request.
        if ((plan as PricedPlan).onRequest) return { kind: 'sales', label: t('PRICING.OFFER.ASK_PRICE'), onClick: () => handleContactSales(plan), disabled: false };
        return { kind, label: t('PRICING.WALL.CHOOSE', { plan: plan.name }), onClick: () => setCheckoutPlan(plan), disabled: false };
      case 'sales': return { kind, label: t('PRICING.UPGRADE.TALK_TO_SALES'), onClick: () => handleContactSales(plan), disabled: false };
      default: return { kind, label: t('PLANS.GET_STARTED', 'Get Started'), onClick: () => setConfirmDialog({ open: true, plan }), disabled: false };
    }
  };

  const breadcrumbs = [
    { label: t('BILLING.TITLE', 'Billing') },
    { label: t('SIDEBAR.UPGRADE', 'Upgrade') },
  ];

  if (loading) {
    return (
      <PageContainer breadcrumbs={breadcrumbs}>
        <div className="flex h-[60vh] items-center justify-center" data-testid="plans-loading">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    );
  }

  const checkoutStatus = new URLSearchParams(location.search).get('checkout');

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <div>
        {checkoutStatus === 'success' && (
          <Alert className="mb-6">
            <CircleCheck className="text-success" />
            <AlertDescription>
              {t('PLANS.CHECKOUT_SUCCESS', 'Payment received — your subscription is being activated. This can take a few seconds; refresh if your plan is not updated yet.')}
            </AlertDescription>
          </Alert>
        )}
        {checkoutStatus === 'cancel' && (
          <Alert className="mb-6">
            <Info />
            <AlertDescription>
              {t('PLANS.CHECKOUT_CANCELED', 'Checkout canceled — no changes were made.')}
            </AlertDescription>
          </Alert>
        )}
        {/* Why the vendor is here: the limit or feature that sent them, and what Pro changes about it. */}
        {(reasonIsLimit || reasonIsFeature) && (
          <div
            data-testid="upgrade-reason"
            data-reason={reason}
            className="mb-6 flex flex-col gap-3 rounded-xl border border-foreground bg-card p-4 sm:flex-row sm:items-center sm:justify-between md:mb-8"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {reasonIsLimit
                  ? t('FREE_PLAN.UPGRADE_PAGE.REASON_TITLE', { what: t(`FREE_PLAN.WHAT.${reason}`) })
                  : t(`FREE_PLAN.FEATURE.${reason}.TITLE`, { defaultValue: t('FREE_PLAN.FEATURE.GENERIC.TITLE') })}
              </p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {reasonIsLimit
                  ? t(`FREE_PLAN.PRO_GETS.${reason}`, { defaultValue: t('FREE_PLAN.PRO_GETS.GENERIC') })
                  : t(`FREE_PLAN.FEATURE.${reason}.BODY`, { defaultValue: t('FREE_PLAN.FEATURE.GENERIC.BODY') })}
              </p>
            </div>
            {reasonIsLimit && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 self-start sm:self-center"
                onClick={() => document.getElementById(`plan-row-${reason}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })}
              >
                {t('FREE_PLAN.UPGRADE_PAGE.REASON_CTA')}
              </Button>
            )}
          </div>
        )}

        {/* Trial running / ended, or the agreement this vendor is on. */}
        <AccessBanner className="mb-6 md:mb-8" />

        {/* Intro */}
        <div className="mb-6 text-center md:mb-8">
          <h2 className="font-heading mb-2 text-2xl font-medium tracking-tight">
            {t('PLANS.PAGE_TITLE', 'Choose your plan')}
          </h2>
          <p className="mx-auto max-w-[520px] text-sm leading-relaxed text-muted-foreground">
            {t(
              'PLANS.PAGE_SUBTITLE',
              'Pick the plan that fits your business. You can upgrade or downgrade at any time.',
            )}
          </p>
          {/* Monthly / yearly — a segmented pill, like every tab. */}
          {plans.some((p) => monthsSaved(p) > 0) && (
            <div className="mt-5 inline-flex items-center gap-1 rounded-full border bg-card p-1" role="tablist" data-testid="interval-toggle">
              {(['month', 'year'] as Interval[]).map((iv) => (
                <button
                  key={iv}
                  type="button"
                  role="tab"
                  aria-selected={interval === iv}
                  data-testid={`interval-${iv}`}
                  onClick={() => setInterval(iv)}
                  className={cn(
                    'rounded-full px-4 py-1.5 text-sm transition-colors',
                    interval === iv ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {iv === 'month' ? t('PRICING.UPGRADE.MONTHLY') : t('PRICING.UPGRADE.YEARLY')}
                  {iv === 'year' && (
                    <span className={cn('ms-2 text-xs', interval === iv ? 'text-background/70' : 'text-muted-foreground')}>
                      {t('PRICING.UPGRADE.SAVE_MONTHS', { count: Math.max(...plans.map(monthsSaved)) })}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Plan cards */}
        <div
          className="mb-12 grid grid-cols-1 gap-6 md:mb-16 md:grid-cols-[repeat(var(--plan-cols),minmax(0,1fr))]"
          style={{ '--plan-cols': Math.min(plans.length, 4) } as React.CSSProperties}
        >
          {plans.map((plan) => {
            const isCurrent = plan.slug === planSlug;
            const cta = getCta(plan, isCurrent);
            // An accepted quote for this plan replaces "Contact sales" with the agreed
            // price — but never for a disabled downgrade.
            const acceptedQuote = cta.kind === 'downgrade' ? undefined : payableQuoteByPlan[plan._id];
            const ctaLabel = acceptedQuote ? quoteMoney(acceptedQuote) : cta.label;
            const ctaOnClick = acceptedQuote ? () => payAcceptedQuote(acceptedQuote) : cta.onClick;
            const ctaDisabled = acceptedQuote ? changing : cta.disabled || changing;
            // The vendor's CURRENT plan is the emphasized card — an ink outline,
            // the way selection reads everywhere else.
            const highlighted = isCurrent;
            const priceLabel = formatPrice(plan);

            return (
              <div
                key={plan._id}
                data-testid={`plan-card-${plan.slug}`}
                data-highlighted={highlighted ? 'true' : 'false'}
                className={cn(
                  'flex flex-col rounded-xl border bg-card p-6 transition-colors',
                  highlighted ? 'border-foreground' : 'hover:border-foreground/40',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xl font-medium">{plan.name}</p>
                  {(plan as PricedPlan).offered && (
                    <span className="rounded-full border border-foreground px-2 py-0.5 text-[11px] font-medium" data-testid={`plan-offered-${plan.slug}`}>
                      {t('PRICING.OFFER.YOUR_PRICE')}
                    </span>
                  )}
                </div>
                <p className="mb-4 mt-1 min-h-[2.5em] text-xs leading-relaxed text-muted-foreground">{plan.tagline || plan.description}</p>

                {/* Price — an accepted quote shows this vendor's agreed price; a
                    custom/sales tier reads "Custom" (no fixed price); free → "Free". */}
                {(() => {
                  const tier = classifyPlan(plan);
                  // A one-time setup charge sits outside the recurring price, so
                  // it is called out separately rather than folded into it.
                  const setupFee = effectiveIntegrationFee(plan, acceptedQuote);
                  const setupNote = setupFee > 0 && (
                    <p className="mb-4 -mt-3 text-xs text-muted-foreground">
                      {t('PLANS.SETUP_FEE', '+ {{fee}} one-time integration fee', {
                        fee: feeMoney(setupFee, acceptedQuote?.quotedCurrency || plan.currency),
                      })}
                    </p>
                  );
                  if (acceptedQuote) {
                    return (
                      <>
                        <p className="font-heading mb-4 text-3xl leading-none font-medium tracking-tight">
                          {quoteMoney(acceptedQuote)}
                        </p>
                        {setupNote}
                      </>
                    );
                  }
                  if ((tier === 'sales' || (plan as PricedPlan).onRequest) && Number(plan.price) > 0) {
                    // Enterprise: a floor, on a 12-month contract (docs/pricing-v2.md).
                    return (
                      <>
                        <div className="mb-1 flex items-baseline gap-1.5">
                          <span className="text-sm text-muted-foreground">{t('PRICING.UPGRADE.FROM')}</span>
                          <span className="font-heading text-3xl leading-none font-medium tracking-tight">{money(Number(plan.price), plan.currency)}</span>
                          <span className="text-sm text-muted-foreground">{t('PRICING.UPGRADE.PER_MONTH')}</span>
                        </div>
                        <p className="mb-4 text-xs text-muted-foreground">{(plan as PricedPlan).onRequest ? t('PRICING.OFFER.ON_REQUEST') : t('PRICING.UPGRADE.MIN_TERM')}</p>
                      </>
                    );
                  }
                  if (tier === 'sales' || tier === 'free') {
                    return (
                      <>
                        <p className="font-heading mb-1 text-3xl leading-none font-medium tracking-tight">
                          {tier === 'sales' ? t('PLANS.PRICE_CUSTOM', 'Custom') : t('PLANS.PRICE_FREE', 'Free')}
                        </p>
                        <p className="mb-4 min-h-[1rem] text-xs text-muted-foreground">
                          {plan.pricingType === 'trial' ? t('PRICING.UPGRADE.TRIAL_DAYS', { count: plan.trialDays || 14 }) : null}
                        </p>
                      </>
                    );
                  }
                  const perMonth = monthlyPrice(plan, interval);
                  if (!perMonth && !priceLabel) return null;
                  return (
                    <>
                      <div className="mb-1 flex items-baseline gap-1.5">
                        <span className="font-heading text-3xl leading-none font-medium tracking-tight">{perMonth ? money(perMonth, plan.currency) : priceLabel}</span>
                        <span className="text-sm text-muted-foreground">{t('PRICING.UPGRADE.PER_MONTH')}</span>
                      </div>
                      <p className="mb-4 text-xs text-muted-foreground">
                        {interval === 'year'
                          ? t('PRICING.UPGRADE.BILLED_YEARLY', { amount: money(Number(plan.annualPrice) || Number(plan.price) * 12, plan.currency) })
                          : t('PRICING.UPGRADE.BILLED_MONTHLY')}
                      </p>
                      {setupNote}
                    </>
                  );
                })()}

                {/* What tells the plans apart: catalog and traffic first, then what it adds.
                    The table below carries every detail. */}
                <ul className="mb-6 flex flex-1 flex-col gap-2.5" data-testid={`plan-highlights-${plan.slug}`}>
                  {planHighlights(plan, t).map((h) => (
                    <li key={h} className="flex items-start gap-2.5 text-sm">
                      <FeatureBadge enabled />
                      <span className="leading-snug">{h}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant="outline"
                  className="w-full"
                  data-testid={`plan-cta-${plan.slug}`}
                  disabled={ctaDisabled}
                  onClick={ctaOnClick}
                >
                  {ctaLabel}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Comparison table */}
        {plans.length > 0 && (
          <div data-testid="plans-comparison-table" className="overflow-hidden rounded-xl border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="ps-6" />
                  {plans.map((plan) => (
                    <TableHead key={plan._id} className="text-center text-sm font-medium text-foreground">
                      {plan.name}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Price row */}
                {plans.some((p) => p.price !== undefined) && (
                  <TableRow>
                    <TableCell className="ps-6 text-sm">{t('PLANS.TABLE.PRICE', 'Price')}</TableCell>
                    {plans.map((plan) => {
                      const label = formatPrice(plan);
                      const tier = classifyPlan(plan);
                      return (
                        <TableCell key={plan._id} className="text-center text-sm">
                          {tier === 'sales'
                            ? Number(plan.price) > 0
                              ? `${t('PRICING.UPGRADE.FROM')} ${money(Number(plan.price), plan.currency)}${t('PRICING.UPGRADE.PER_MONTH')}`
                              : t('PLANS.PRICE_CUSTOM', 'Custom')
                            : tier === 'free'
                              ? t('PLANS.PRICE_FREE', 'Free')
                              : label
                                ? `${label}/${t('PLANS.PER_MONTH', 'month')}`
                                : '—'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}

                {/* Grouped, collapsible sections — one accordion per product module
                    (expanded by default). Header row shows module inclusion; the
                    detail rows show limits, quality and sub-features. */}
                {MODULE_GROUPS.map((g) => {
                  const open = !collapsedGroups[g.id];
                  const rows = groupDetailRows(g);
                  return (
                    <React.Fragment key={g.id}>
                      <TableRow
                        data-testid={`table-group-${g.id}`}
                        onClick={() => toggleGroup(g.id)}
                        className="cursor-pointer bg-muted/50 hover:bg-muted"
                      >
                        <TableCell className="ps-6">
                          <div className="flex items-center gap-3">
                            <GroupMark accent={g.accent} Icon={g.Icon} size={24} />
                            <span className="flex-1 text-sm font-medium">{groupTitle(g)}</span>
                            {open ? <ChevronUp className="size-4 text-muted-foreground" /> : <ChevronDown className="size-4 text-muted-foreground" />}
                          </div>
                        </TableCell>
                        {plans.map((plan) => (
                          <TableCell key={plan._id} className="text-center">
                            {tableCheck(g.feature ? Boolean(plan.features?.[g.feature]) : true)}
                          </TableCell>
                        ))}
                      </TableRow>
                      {(open || rows.some((row) => row.key === reason)) && rows.map((row) => (
                        <TableRow
                          key={row.key}
                          id={`plan-row-${row.key}`}
                          data-highlighted={row.key === reason ? 'true' : undefined}
                          className={cn(row.key === reason && 'bg-accent/60')}
                        >
                          <TableCell className={cn('ps-12 pe-6 text-sm', row.key === reason ? 'font-medium text-foreground' : 'text-muted-foreground')}>{row.label}</TableCell>
                          {plans.map((plan) => (
                            <TableCell key={plan._id} className="text-center text-sm">
                              {row.render(plan)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}

                {/* The free plan's limits: row caps, monthly meters and history — the
                    depth that separates Free from Pro, since every feature is open on both. */}
                {(() => {
                  const open = !collapsedGroups.limits || NEW_LIMIT_KEYS.includes(reason as NewLimitKey);
                  return (
                    <React.Fragment>
                      <TableRow
                        data-testid="table-group-limits"
                        onClick={() => toggleGroup('limits')}
                        className="cursor-pointer bg-muted/50 hover:bg-muted"
                      >
                        <TableCell className="ps-6">
                          <div className="flex items-center gap-3">
                            <span className="w-6 shrink-0" />
                            <span className="flex-1 text-sm font-medium">{t('FREE_PLAN.UPGRADE_PAGE.LIMITS_GROUP')}</span>
                            {open ? <ChevronUp className="size-4 text-muted-foreground" /> : <ChevronDown className="size-4 text-muted-foreground" />}
                          </div>
                        </TableCell>
                        {plans.map((plan) => (
                          <TableCell key={plan._id} />
                        ))}
                      </TableRow>
                      {open && NEW_LIMIT_KEYS.map((key) => (
                        <TableRow
                          key={key}
                          id={`plan-row-${key}`}
                          data-testid={`limit-row-${key}`}
                          data-highlighted={key === reason ? 'true' : undefined}
                          className={cn(key === reason && 'bg-accent/60')}
                        >
                          <TableCell className={cn('ps-12 pe-6 text-sm', key === reason ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                            {t(`FREE_PLAN.LIMITS.${key}`)}
                          </TableCell>
                          {plans.map((plan) => (
                            <TableCell key={plan._id} className="text-center text-sm tabular-nums">
                              {formatPlanLimit(plan, key)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })()}

                {/* Add-ons group */}
                {(() => {
                  const open = !collapsedGroups.addons;
                  return (
                    <React.Fragment>
                      <TableRow
                        data-testid="table-group-addons"
                        onClick={() => toggleGroup('addons')}
                        className="cursor-pointer bg-muted/50 hover:bg-muted"
                      >
                        <TableCell className="ps-6">
                          <div className="flex items-center gap-3">
                            <span className="w-6 shrink-0" />
                            <span className="flex-1 text-sm font-medium">{t('QUOTE.ADDONS', 'Add-ons')}</span>
                            {open ? <ChevronUp className="size-4 text-muted-foreground" /> : <ChevronDown className="size-4 text-muted-foreground" />}
                          </div>
                        </TableCell>
                        {plans.map((plan) => (
                          <TableCell key={plan._id} />
                        ))}
                      </TableRow>
                      {open && ADDON_GROUPS.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="ps-12 pe-6">
                            <div className="flex items-center gap-3">
                              <GroupMark Icon={a.Icon} size={24} />
                              <span className="text-sm text-muted-foreground">{addonTitle(a.id)}</span>
                            </div>
                          </TableCell>
                          {plans.map((plan) => (
                            <TableCell key={plan._id} className="text-center">
                              {tableCheck(addonEnabled(plan, a.keys))}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })()}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Confirm dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(next) => { if (!next && !changing) setConfirmDialog({ open: false }); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('PLANS.CONFIRM_CHANGE_TITLE', 'Change plan')}</DialogTitle>
              <DialogDescription>
                {t('PLANS.CONFIRM_CHANGE_TEXT', 'Are you sure you want to switch to the {{planName}} plan?', {
                  planName: confirmDialog.plan?.name,
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" className="text-muted-foreground" onClick={() => setConfirmDialog({ open: false })} disabled={changing}>
                {t('COMMON.CANCEL', 'Cancel')}
              </Button>
              <Button
                variant="outline"
                onClick={() => confirmDialog.plan && handleChangePlan(confirmDialog.plan)}
                disabled={changing}
              >
                {changing ? <Loader2 className="size-4 animate-spin" /> : t('PLANS.CONFIRM_CHANGE', 'Confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment-method chooser (Card → Stripe embedded; Wire → manual invoice) */}
        <PaymentMethodModal
          plan={checkoutPlan}
          initialInterval={interval}
          onClose={() => setCheckoutPlan(null)}
          onChooseCard={(planId, interval) => { setCheckoutPlan(null); setUpgradeInterval(interval); setUpgradePlanId(planId); }}
        />

        <EmbeddedUpgradeModal
          planId={upgradePlanId}
          interval={upgradeInterval}
          onClose={() => setUpgradePlanId(null)}
          onComplete={handleUpgradeComplete}
        />

        {/* Embedded checkout for an accepted (Stripe) quote's custom price */}
        <EmbeddedUpgradeModal
          quoteId={payQuoteId}
          onClose={() => setPayQuoteId(null)}
          onComplete={handleUpgradeComplete}
        />

        {/* Enterprise/sales-tier quote configurator (replaces the mailto CTA) */}
        <QuoteConfiguratorModal plan={quotePlan} onClose={() => setQuotePlan(null)} />
      </div>
    </PageContainer>
  );
};

export default UpgradePage;
